import { useState, useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  XMarkIcon,
  ListBulletIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PencilIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import Pagination from "../../common/Pagination";
import { legislationsAPI } from "../../../services/api";

export default function CommitteeManagement({ setOpen }) {
  const [committees, setCommittees] = useState([]);
  const [chairmanshipOptions, setChairmanshipOptions] = useState([]);
  const [showCommitteeModal, setShowCommitteeModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newCommittee, setNewCommittee] = useState({
    name: "",
    description: "",
    chairmanship: null,
  });
  const [editingCommitteeId, setEditingCommitteeId] = useState(null);
  const [chairOpen, setChairOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // --- Fetch Committees ---
  const fetchCommittees = async (
    page = 1,
    perPage = itemsPerPage,
    search = searchTerm
  ) => {
    try {
      const response = await legislationsAPI.getCommittees({
        per_page: perPage.toString(),
        page: page.toString(),
        search: search || "",
      });

      const data = response.data.data.legislative_documents || [];
      const meta = response.data.data.meta || { total: 0 };

      setCommittees(data);
      setTotalItems(parseInt(meta.total, 10));
    } catch (err) {
      console.error("Error fetching committees:", err);
      toast.error("Failed to fetch committees");
    }
  };

  // --- Fetch Chairmanship Options ---
  const fetchChairmanshipOptions = async () => {
    try {
      const response = await legislationsAPI.getChairmanships();
      if (response.data.success && Array.isArray(response.data.data)) {
        setChairmanshipOptions(response.data.data);
      } else {
        toast.error("Failed to load chairmanships");
      }
    } catch (err) {
      console.error("Error fetching chairmanships:", err);
      toast.error("Failed to load chairmanships");
    }
  };

  useEffect(() => {
    fetchCommittees(currentPage, itemsPerPage, searchTerm);
    fetchChairmanshipOptions();
  }, [currentPage, itemsPerPage, searchTerm]);

  // --- Archive / Restore ---
  const handleArchiveCommittee = async (committeeId) => {
    try {
      const response = await legislationsAPI.archiveCommittee(committeeId);
      if (response.data.success) {
        toast.success(response.data.message || "Committee archived successfully!");
        setCommittees((prev) =>
          prev.map((c) =>
            c.committee_id === committeeId
              ? { ...c, deleted_at: new Date().toISOString() }
              : c
          )
        );
      } else {
        toast.error(response.data.message || "Failed to archive committee");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to archive committee. Please try again.");
    }
  };

  const handleRestoreCommittee = async (committeeId) => {
    try {
      const response = await legislationsAPI.restoreCommittee(committeeId);
      if (response.data.success) {
        toast.success(response.data.message || "Committee restored successfully!");
        setCommittees((prev) =>
          prev.map((c) =>
            c.committee_id === committeeId ? { ...c, deleted_at: null } : c
          )
        );
      } else {
        toast.error(response.data.message || "Failed to restore committee");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to restore committee. Please try again.");
    }
  };

  const handleSave = async () => {
    if (!newCommittee.name) return toast.error("Committee Name is required");

    const payload = {
      committee_name: newCommittee.name,
      description: newCommittee.description || null,
      chairmanship_id:
        newCommittee.chairmanship && Number(newCommittee.chairmanship.value) > 0
          ? Number(newCommittee.chairmanship.value)
          : null,
    };

    try {
      if (editingCommitteeId) {
        const response = await legislationsAPI.updateCommittee(
          editingCommitteeId,
          payload
        );
        if (response.data.success) {
          toast.success(response.data.message || "Committee updated successfully!");
          setEditingCommitteeId(null);
          setNewCommittee({ name: "", description: "", chairmanship: null });
          fetchCommittees(currentPage, itemsPerPage);
          fetchCommittees(currentPage, itemsPerPage);
        } else {
          toast.error(response.data.message || "Failed to update committee");
        }
      } else {
        const response = await legislationsAPI.createCommittee(payload);
        if (response.data.success) {
          toast.success(response.data.message || "Committee created successfully!");
          setNewCommittee({ name: "", description: "", chairmanship: null });
          fetchCommittees(currentPage, itemsPerPage);
        } else {
          toast.error(response.data.message || "Failed to create committee");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save committee. Please try again.");
    }
  };

  const handleClear = () => {
    setNewCommittee({ name: "", description: "", chairmanship: null });
    setEditingCommitteeId(null);
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      {/* MAIN MODAL */}
      <div className="fixed inset-0 z-50 flex justify-center overflow-y-auto py-10">
        <div
          className="fixed inset-0 bg-black/30"
          onClick={() => setOpen(false)}
        ></div>

        <div className="relative bg-white rounded-2xl w-full max-w-4xl shadow-xl p-6 my-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-800">
              Committee Management
            </h2>
            <button onClick={() => setOpen(false)}>
              <XMarkIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-full">
                <ListBulletIcon className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-lg font-semibold text-gray-800">
                List of Committees
              </span>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Search committee..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                onClick={() => {
                  handleClear();
                  setShowCommitteeModal(true);
                }}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                <PlusIcon className="w-5 h-5" />
                New Committee
              </button>
            </div>
          </div>

          {/* Table */}
          <div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    COMMITTEE NAME
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DESCRIPTION
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {committees.map((c, idx) => (
                  <tr
                    key={idx}
                    className="border-t hover:bg-gray-50 transition cursor-pointer"
                  >
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {c.committee_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {c.description}
                    </td>
                    <td className="px-6 py-4 flex items-center justify-center gap-4">
                      {!c.deleted_at && (
                        <button
                          className="text-orange-400 hover:text-orange-600"
                          onClick={() => {
                            setNewCommittee({
                              name: c.committee_name,
                              description: c.description || "",
                              chairmanship: c.chairmanship
                                ? {
                                  value: c.chairmanship.user_id,
                                  label: c.chairmanship.chairman,
                                }
                                : null,
                            });
                            setEditingCommitteeId(c.committee_id);
                            setShowCommitteeModal(true);
                          }}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                      {c.deleted_at ? (
                        <button
                          className="text-blue-600 hover:text-blue-800"
                          onClick={() => handleRestoreCommittee(c.committee_id)}
                        >
                          <ArrowPathIcon className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleArchiveCommittee(c.committee_id)}
                        >
                          <ArchiveBoxIcon className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              lastPage={totalPages}
              perPage={itemsPerPage}
              total={totalItems}
              onPageChange={setCurrentPage}
              onPerPageChange={setItemsPerPage}
            />
          </div>
        </div>
      </div>

      {/* CREATE / EDIT COMMITTEE MODAL */}
      {showCommitteeModal && (
        <div className="fixed inset-0 z-50 flex justify-center overflow-y-auto py-10">
          <div
            className="fixed inset-0 bg-black/30"
            onClick={() => {
              setShowCommitteeModal(false);
              handleClear();
            }}
          ></div>

          <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-xl my-auto flex flex-col max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-800">
                {editingCommitteeId ? "Edit Committee" : "Create New Committee"}
              </h2>
              <button
                onClick={() => {
                  setShowCommitteeModal(false);
                  handleClear();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6 overflow-y-auto">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Committee Name
                </label>
                <input
                  type="text"
                  placeholder="Enter committee name"
                  className="w-full border border-gray-300 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newCommittee.name}
                  onChange={(e) =>
                    setNewCommittee({ ...newCommittee, name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Description
                </label>
                <textarea
                  placeholder="Enter short description"
                  rows={4}
                  className="w-full border border-gray-300 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newCommittee.description}
                  onChange={(e) =>
                    setNewCommittee({
                      ...newCommittee,
                      description: e.target.value,
                    })
                  }
                />
              </div>
              <div className="relative">
                <label className="block text-gray-700 font-medium mb-2">
                  Chairmanship
                </label>
                <div
                  className="border border-gray-300 rounded-xl p-3 flex justify-between items-center cursor-pointer"
                  onClick={() => setChairOpen(!chairOpen)}
                >
                  <span className="text-sm text-gray-600">
                    {newCommittee.chairmanship?.label || "Select chairmanship"}
                  </span>
                  {chairOpen ? (
                    <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                  )}
                </div>
                {chairOpen && (
                  <ul className="absolute w-full bg-white border border-gray-200 rounded-xl mt-2 shadow-lg z-20 max-h-52 overflow-y-auto">
                    {chairmanshipOptions.map((opt) => (
                      <li
                        key={opt.value}
                        className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setNewCommittee({ ...newCommittee, chairmanship: opt });
                          setChairOpen(false);
                        }}
                      >
                        {opt.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-4 px-6 py-4 border-t flex-shrink-0 bg-gray-50">
              <button
                onClick={() => {
                  setShowCommitteeModal(false);
                  handleClear();
                }}
                className="px-6 py-2 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition"
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  await handleSave();
                  setShowCommitteeModal(false);
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition"
              >
                {editingCommitteeId ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
